import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Textarea } from '@/components/ui/textarea'
import { Trash2, Copy, Eye, EyeOff } from 'lucide-react'

interface DescriptionTemplateModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentDescription: string
  setDescription: (desc: string) => void
  hasExistingContent?: boolean
}

interface Template {
  id: string
  name: string
  content: string
  createdAt: string
}

export function DescriptionTemplateModal({
  open,
  onOpenChange,
  currentDescription,
  setDescription,
  hasExistingContent = false,
}: DescriptionTemplateModalProps) {
  const [templates, setTemplates] = useState<Template[]>([])
  const [newTemplateName, setNewTemplateName] = useState('')
  const [previewTemplate, setPreviewTemplate] = useState<string | null>(null)
  const [showPreview, setShowPreview] = useState<{ [key: string]: boolean }>({})

  useEffect(() => {
    try {
      const stored = localStorage.getItem('descriptionTemplates')
      if (stored) {
        const parsed = JSON.parse(stored)
        if (Array.isArray(parsed)) {
          // Sort by creation date, newest first
          const sorted = parsed.sort((a, b) => 
            new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime()
          )
          setTemplates(sorted)
        }
      }
    } catch (err) {
      console.error('Failed to load templates:', err)
    }
  }, [open])

  const handleSaveTemplate = () => {
    const name = newTemplateName.trim()
    const content = currentDescription.trim()

    if (!name) {
      alert('Please enter a template name')
      return
    }
    
    if (!content) {
      alert('No content in description to save as template')
      return
    }

    // Check if template with same name already exists
    const existingIndex = templates.findIndex(t => t.name.toLowerCase() === name.toLowerCase())
    
    if (existingIndex !== -1) {
      if (!confirm(`A template named "${name}" already exists. Replace it?`)) {
        return
      }
    }

    const newEntry: Template = {
      id: crypto.randomUUID(),
      name,
      content,
      createdAt: new Date().toISOString()
    }

    let updated: Template[]
    if (existingIndex !== -1) {
      // Replace existing template
      updated = [...templates]
      updated[existingIndex] = newEntry
    } else {
      // Add new template at the beginning
      updated = [newEntry, ...templates]
    }
    
    setTemplates(updated)
    localStorage.setItem('descriptionTemplates', JSON.stringify(updated))
    setNewTemplateName('')
    
    // Show success feedback
    console.log(`✅ Template "${name}" saved successfully (${content.length} characters)`)
  }

  const handleInsertTemplate = (template: Template) => {
    if (hasExistingContent && currentDescription.trim()) {
      const action = confirm(
        `This will replace your current description (${currentDescription.length} characters) with the template "${template.name}" (${template.content.length} characters). Continue?`
      )
      if (!action) return
    }
    
    setDescription(template.content)
    console.log(`✅ Template "${template.name}" inserted (${template.content.length} characters)`)
    
    // Close modal after successful insertion
    onOpenChange(false)
  }

  const handleAppendTemplate = (template: Template) => {
    const current = currentDescription.trim()
    const separator = current ? '\n\n' : ''
    const newDescription = current + separator + template.content
    
    setDescription(newDescription)
    console.log(`✅ Template "${template.name}" appended`)
    
    // Close modal after successful append
    onOpenChange(false)
  }

  const handleDelete = (template: Template) => {
    if (!confirm(`Delete template "${template.name}"?`)) return
    
    const filtered = templates.filter(t => t.id !== template.id)
    setTemplates(filtered)
    localStorage.setItem('descriptionTemplates', JSON.stringify(filtered))
    
    console.log(`🗑️ Template "${template.name}" deleted`)
  }

  const togglePreview = (templateId: string) => {
    setShowPreview(prev => ({
      ...prev,
      [templateId]: !prev[templateId]
    }))
  }

  const copyToClipboard = (content: string, name: string) => {
    navigator.clipboard.writeText(content).then(() => {
      console.log(`📋 Template "${name}" copied to clipboard`)
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] flex flex-col">
        <DialogTitle className="text-lg font-semibold">Description Templates</DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground">
          Save and reuse common description layouts. Current description: {currentDescription.length} characters.
        </DialogDescription>

        {/* Save current description as template */}
        <div className="flex gap-2 mt-4 p-4 bg-muted/30 rounded-lg">
          <Input
            placeholder="Name this template..."
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                handleSaveTemplate()
              }
            }}
          />
          <Button 
            onClick={handleSaveTemplate} 
            variant="default"
            disabled={!currentDescription.trim() || !newTemplateName.trim()}
          >
            Save Current ({currentDescription.length} chars)
          </Button>
        </div>

        {/* Templates list */}
        <ScrollArea className="flex-1 mt-4">
          {templates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">No saved templates yet.</p>
              <p className="text-xs mt-1">Enter a name above and click "Save Current" to create your first template.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="p-4 bg-card border rounded-lg space-y-3"
                >
                  {/* Template header */}
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{template.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {template.content.length} characters • {new Date(template.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => togglePreview(template.id)}
                        title={showPreview[template.id] ? "Hide preview" : "Show preview"}
                      >
                        {showPreview[template.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(template.content, template.name)}
                        title="Copy to clipboard"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(template)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete template"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Preview (collapsible) */}
                  {showPreview[template.id] && (
                    <div className="border-t pt-3">
                      <Textarea
                        value={template.content}
                        readOnly
                        className="min-h-[100px] text-xs font-mono bg-muted/50 resize-none"
                      />
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleInsertTemplate(template)}
                      className="flex-1"
                    >
                      Replace Description
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleAppendTemplate(template)}
                      className="flex-1"
                      disabled={!currentDescription.trim()}
                    >
                      Append to Current
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}